import { Observable, of, Subject } from "rxjs";
import axios, {
  AxiosRequestConfig,
  AxiosResponse,
  AxiosPromise,
  AxiosError,
} from "axios";
import { stringify } from "query-string";
import base64url from "base64url";
import env from "../../environments/env.json";
import { Studio } from "./Studio";

export interface ZnaRequestError<T = any> extends AxiosError<T> {
  config: AxiosRequestConfig;
  code?: string;
  request?: any;
  response?: AxiosResponse<T>;
  isAxiosError: boolean;
  isCognitoError: boolean;
  toJSON: () => object;
}

function ucfirst(str: string) {
  return str.substring(0, 1).toUpperCase() + str.substring(1);
}

function getStudioKuerzel(studioBezeichner: string): string {
  if (studioBezeichner.indexOf("-") > -1) {
    return studioBezeichner
      .split("-")
      .map((str) => str.substring(0, 2))
      .map(ucfirst)
      .join("-");
  } else {
    return studioBezeichner.substring(0, 2).toUpperCase();
  }
}

export type Zugriffsart = "Lesezugriff" | "Schreibzugriff";

export function generateRollenname(
  studio: Studio | string | null,
  modulName: string | null,
  zugriffsart: Zugriffsart
) {
  let rolle = "";
  if (studio != null) {
    let studioBezeichner = "";
    if (typeof studio == "string") {
      studioBezeichner = studio;
    } else if (typeof studio == "object") {
      studioBezeichner = studio.bezeichner;
    }
    const studioKuerzel = getStudioKuerzel(studioBezeichner);
    rolle = `Studio-${studioKuerzel}/`;
  }
  if (modulName != null) {
    rolle += `${ucfirst(modulName)}/`;
  }
  rolle += ucfirst(zugriffsart);
  return rolle;
}

const _getCognitoBaseUrl = () => {
  return `https://${env.auth.cognitoHost}`;
};
const _getCognitoTokenEndpoint = () => {
  return _getCognitoBaseUrl() + "/oauth2/token";
};
const _getCognitoAuthorizeEndpoint = () => {
  return _getCognitoBaseUrl() + "/oauth2/authorize";
};
const _getCognitoLogoutEndpoint = () => {
  return _getCognitoBaseUrl() + "/logout";
};
const _getApiAuthCredentials = () => {
  // TODO: Aus LocalStorage heraussuchen
  return `${env.auth.clientId}:${env.auth.clientSecret}`;
};
const _getApiBaseUrl = () => {
  return `https://${env.api.host}`;
};

// Axios Config
const httpClient = axios.create({ baseURL: _getApiBaseUrl() });

const authErrors = new Subject<any>();
const successfulCalls = new Subject<any>();

const STORAGE_KEY_VALID_UNTIL = "auth-token-valid-until";
const STORAGE_KEY_ACCESS_TOKEN = "auth-token";
const STORAGE_KEY_ACCESS_TOKEN_SCOPE = "auth-token-scope";
const STORAGE_KEY_ACCESS_TOKEN_TYPE = "auth-token-type";
const STORAGE_KEY_REFRESH_TOKEN = "refresh-token";
const STORAGE_KEY_ID_TOKEN = "id-token";
const STORAGE_KEY_NAME = "id-name";

function _checkOrUpdateTokenScope(
  request: AxiosRequestConfig
): AxiosRequestConfig | Promise<AxiosRequestConfig> {
  // Read Token and Scope
  const authTokenValid =
    parseInt(localStorage.getItem(STORAGE_KEY_VALID_UNTIL) || "") > Date.now();
  const authToken = localStorage.getItem(STORAGE_KEY_ACCESS_TOKEN);
  const authTokenScope =
    localStorage.getItem(STORAGE_KEY_ACCESS_TOKEN_SCOPE) || "";
  console.log("Auth-Token: " + (authToken ? "vorhanden" : "fehlt"));
  console.log("Auth-Token hat Scope: " + authTokenScope);
  console.log("Auth-Token gültig: " + authTokenValid);

  // Check validity
  const requestConfig: any = request;
  const requiredScope = (requestConfig.requiredScope as string) || "";
  const allScopesGranted = requiredScope
    .split(" ")
    .reduce(
      (previousValue, currentValue) =>
        previousValue && authTokenScope.indexOf(currentValue) != -1,
      true
    );
  console.log("Benötigter Scope: " + requiredScope);
  console.log("Kompletter Scope erfüllt: " + allScopesGranted);
  if (allScopesGranted && authTokenValid) {
    // Token sollte noch gültig sein und hat benötigten Scope
    request.headers.Authorization = authToken;
    return request;
  }
  const refreshToken = localStorage.getItem(STORAGE_KEY_REFRESH_TOKEN);
  if (!refreshToken) {
    console.log("Refresh ohne Refresh-Token verhindert");
    return request;
  }

  // Broader Scope is needed or token expired
  const desiredScope = `${authTokenScope} ${requiredScope}`
    .trim()
    .split(" ")
    .filter(
      (value, index, self) => value != " " && self.indexOf(value) === index
    )
    .join(" ");
  console.log(
    `Token hat Scope "${authTokenScope}", benötigt wird "${requiredScope}"\n--> Beantrage Token mit Scope "${desiredScope}"`
  );

  const postParams: any = {
    scope: desiredScope,
  };
  // postParams["grant_type"] = "client_credentials";
  postParams["grant_type"] = "refresh_token";
  postParams["refresh_token"] = refreshToken;

  //const authString = btoa(_getApiAuthCredentialsOld());
  const authString = btoa(_getApiAuthCredentials());
  const headers = {
    Authorization: "Basic " + authString,
    Accept: "*/*",
    "Content-Type": "application/x-www-form-urlencoded",
  };

  return new Promise((resolve, reject) => {
    if (!postParams["refresh_token"]) {
      authErrors.next({
        request: { status: 400 },
        response: { data: "Kein Refresh-Token vorhanden" },
      });
      return reject({
        response: "Kein Refresh-Token vorhanden",
        isAxiosError: true,
      });
    }

    axios
      .post(
        _getCognitoTokenEndpoint(),
        stringify(postParams).replace("%20", " "),
        {
          headers,
        }
      )
      .then((authResponse) => {
        // console.log(authResponse);
        const authorizationTokenType = authResponse.data.token_type;
        const authorizationToken = authResponse.data.access_token;
        const encodedAccessTokenPayload = authResponse.data.access_token.split(
          "."
        )[1];
        const accessTokenPayload: any = JSON.parse(
          base64url.decode(encodedAccessTokenPayload)
        );
        const idTokenPayload: any = base64url.decode(
          authResponse.data.id_token.split(".")[1]
        );
        const idToken = JSON.parse(idTokenPayload);
        const grantedScopes = accessTokenPayload.scope.split(" ");
        const missingScopes = desiredScope
          .split(" ")
          .filter((desired) => !grantedScopes.includes(desired))
          .join(" ");
        if (missingScopes.length) {
          const reason = `Es wurde zwar ein Token ausgestellt, es fehlen jedoch die angefragten Scopes ${missingScopes}`;
          console.error(reason);
          return reject(reason);
        }

        // Okay! Alle angefragten Scopes wurden auch erhalten
        localStorage.setItem(
          STORAGE_KEY_ACCESS_TOKEN_SCOPE,
          accessTokenPayload.scope
        );
        localStorage.setItem(
          STORAGE_KEY_VALID_UNTIL,
          "" + (authResponse.data.expires_in * 1000 + Date.now() - 5000)
        );
        localStorage.setItem(
          STORAGE_KEY_ACCESS_TOKEN_TYPE,
          authorizationTokenType
        );
        localStorage.setItem(STORAGE_KEY_ACCESS_TOKEN, authorizationToken);
        localStorage.setItem(
          STORAGE_KEY_REFRESH_TOKEN,
          authResponse.data.refresh_token
        );
        localStorage.setItem(STORAGE_KEY_ID_TOKEN, idTokenPayload);
        localStorage.setItem(
          STORAGE_KEY_NAME,
          `${idToken.given_name || ""} ${idToken.name || ""}`.trim()
        );

        request.headers.Authorization = `${authorizationTokenType} ${authorizationToken}`;
        resolve(request);
      })
      .catch((error) => {
        // console.log(error);
        reject(error);
      });
  });
}

function _unwrapAxiosResponse<T>(
  response: AxiosPromise<T>,
  resultSubject?: Subject<T>
): Observable<T> {
  const promiseOutputSubject = resultSubject ? resultSubject : new Subject<T>();
  response
    .then((response: any) => {
      if (response.isAxiosError) {
        promiseOutputSubject.error(response);
      } else if (response.data) {
        if (response.data.body) {
          promiseOutputSubject.next(JSON.parse(response.data.body));
        } else {
          promiseOutputSubject.next(response.data);
        }
        promiseOutputSubject.complete();
      } else {
        promiseOutputSubject.next(response);
        promiseOutputSubject.complete();
      }
    })
    .catch((error) => {
      promiseOutputSubject.error(error);
    });
  return promiseOutputSubject;
}

httpClient.interceptors.request.use(
  (request: AxiosRequestConfig) => {
    //console.log(request);
    if (request.url && request.url.indexOf(_getCognitoBaseUrl()) >= 0) {
      // Requests an AWS Cognito werden nicht geprüft
      return request;
    }

    return _checkOrUpdateTokenScope(request);
  },
  (error) => {
    // Do something with response error
    return Promise.reject(error);
  }
);

httpClient.interceptors.response.use(
  (response: AxiosResponse<any>) => {
    successfulCalls.next(response);
    return response;
  },
  (error: AxiosError<any>) => {
    const baseUrl = _getApiBaseUrl();
    const znaError: ZnaRequestError = {
      config: error.config,
      request: error.request,
      response: error.response,
      code: error.code,
      name: error.name,
      message: error.message,
      stack: error.stack,
      isAxiosError: error.isAxiosError,
      isCognitoError: false,
      toJSON: () => error.toJSON(),
    };
    if (
      error.config &&
      error.config.url &&
      error.config.url.indexOf(_getCognitoBaseUrl()) == 0
    ) {
      // Cognito-Request fehlgeschlagen :(
      console.log("Cognito-Request fehlgeschlagen :(");
      znaError.isCognitoError = true;
      authErrors.next(znaError);
    } else if (error.config && error.config.baseURL == baseUrl) {
      // API-Request fehlgeschlagen :(
      console.log("API-Request fehlgeschlagen :(");
      // Soll sich der Client neu authentifizieren?
      console.error(znaError);
      authErrors.next(znaError);
    }
    return error;
  }
);

function registerAuthErrorObserver(
  next?: (value: any) => void,
  error?: (error: ZnaRequestError) => void,
  complete?: () => void
) {
  return authErrors.subscribe(next, error, complete);
}

function registerSuccessObserver(
  next?: (value: any) => void,
  error?: (error: ZnaRequestError) => void,
  complete?: () => void
) {
  return successfulCalls.subscribe(next, error, complete);
}

export {
  authErrors,
  successfulCalls,
  httpClient,
  getStudioKuerzel,
  registerAuthErrorObserver,
  registerSuccessObserver,
  _getApiAuthCredentials,
  _getCognitoAuthorizeEndpoint,
  _getCognitoTokenEndpoint,
  _getCognitoLogoutEndpoint,
  _unwrapAxiosResponse,
  STORAGE_KEY_VALID_UNTIL,
  STORAGE_KEY_ACCESS_TOKEN,
  STORAGE_KEY_ACCESS_TOKEN_SCOPE,
  STORAGE_KEY_ACCESS_TOKEN_TYPE,
  STORAGE_KEY_REFRESH_TOKEN,
  STORAGE_KEY_ID_TOKEN,
  STORAGE_KEY_NAME,
};
